import { useEffect, useRef, useState } from "react";

interface AudioWavePlaybackProps {
  audio: HTMLAudioElement | Blob;
  height?: number;
  barColor?: string;
  paleColor?: string;
  barCount?: number;
  currentTime?: number;
}

export const AudioWavePlayback: React.FC<AudioWavePlaybackProps> = ({
  audio,
  height = 60,
  barColor = "#1677ff",
  paleColor = "#e0e0e0",
  barCount = 50,
  currentTime = 0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [duration, setDuration] = useState(0);

  // Extract waveform data from audio
  useEffect(() => {
    const extractWaveform = async () => {
      try {
        const audioContext = new AudioContext();
        let arrayBuffer: ArrayBuffer;

        if (audio instanceof Blob) {
          arrayBuffer = await audio.arrayBuffer();
        } else if (audio instanceof HTMLAudioElement && audio.src) {
          const response = await fetch(audio.src);
          arrayBuffer = await response.arrayBuffer();
        } else {
          return;
        }

        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        setDuration(audioBuffer.duration);

        const channelData = audioBuffer.getChannelData(0);
        const samples: number[] = [];
        const sampleSize = Math.floor(channelData.length / barCount);

        for (let i = 0; i < barCount; i++) {
          const start = i * sampleSize;
          const end = start + sampleSize;

          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += Math.abs(channelData[j]);
          }
          samples.push(sum / sampleSize);
        }

        // Normalize samples
        const maxSample = Math.max(...samples);
        const normalized = samples.map((s) => s / maxSample);
        setWaveformData(normalized);

        void audioContext.close();
      } catch (error) {
        console.error("Error extracting waveform:", error);
      }
    };

    void extractWaveform();
  }, [audio, barCount]);

  // Draw waveform with progress
  useEffect(() => {
    if (!canvasRef.current || waveformData.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.offsetWidth;
    canvas.width = width;

    ctx.clearRect(0, 0, width, height);

    const barWidth = width / barCount;
    const gap = 2;
    const progress = duration > 0 ? currentTime / duration : 0;

    for (let i = 0; i < waveformData.length; i++) {
      const barHeight = waveformData[i] * height * 0.85 + 4;
      const x = i * barWidth;
      const y = (height - barHeight) / 2;

      // Color based on progress
      const barProgress = i / barCount;
      ctx.fillStyle = barProgress <= progress ? barColor : paleColor;

      ctx.beginPath();
      ctx.roundRect(x + gap / 2, y, barWidth - gap, barHeight, 3);
      ctx.fill();
    }
  }, [
    waveformData,
    currentTime,
    duration,
    height,
    barColor,
    paleColor,
    barCount,
  ]);

  return (
    <canvas
      ref={canvasRef}
      height={height}
      style={{ width: "100%", display: "block" }}
    />
  );
};
